import { useState } from "react";
import { Link } from "react-router-dom";
import { changePracticeStandard, setPracticeSameAs } from "@/api/endpoints";
import { ApiError } from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Markdown } from "@/components/ui/Markdown";
import { PillButton } from "@/components/ui/PillButton";
import { Select } from "@/components/ui/Select";
import { FlagsSection } from "@/components/FlagsSection";
import { RadarSection } from "@/components/RadarSection";
import type { Practice, Standard } from "@/api/types";

// A radar practice (Techniques / Patterns) in full — the Tools page's detail view for a
// practice card. Practices are recognised ways to meet a standard, suggested under "May help"
// in a map; this is where one is marked reviewed and its drafted standard links decided.

const HEADING = "mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase";

/** One practice: anything needing attention, what it is, the standard it's the same subject as
 * (set, changed or cleared here — offered only standards no other practice already is), the
 * standards it's a recognised way to meet (confirmed, then drafted ones with Confirm /
 * Dismiss), and its Tech Radar card.
 * Pills search the side list (`onSearch`); `onChanged` reloads after an action. A link change
 * sends the practice's `updated_at`, so one made after someone else's edit is refused (409). */
export function PracticeDetail({
  practice: p,
  standards,
  onChanged,
  onSearch,
}: {
  practice: Practice;
  standards: Standard[];
  onChanged: () => void;
  onSearch: (field: string, value: string, toggle?: boolean) => void;
}) {
  const standardNames = new Map(standards.map((s) => [s.id, s.name]));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [choosing, setChoosing] = useState<string | null>(null);

  /** Sets (or, with null, clears) the standard this practice is the same subject as. */
  async function saveSameAs(standardId: string | null) {
    setError(null);
    setBusy(true);
    try {
      await setPracticeSameAs(p.id, standardId, p.updated_at);
      setChoosing(null);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.errors.join("; ") : String(e));
    } finally {
      setBusy(false);
    }
  }

  /** Confirms or dismisses one drafted standard link, then reloads. */
  async function change(action: "confirm" | "dismiss", standardId: string) {
    setError(null);
    setBusy(true);
    try {
      await changePracticeStandard(p.id, action, standardId, p.updated_at);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.errors.join("; ") : String(e));
    } finally {
      setBusy(false);
    }
  }

  const standardLink = (sid: string) => (
    <Link to={`/standards/${encodeURIComponent(sid)}`} className="text-accent underline">
      {standardNames.get(sid) ?? sid}
    </Link>
  );

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-[family-name:--font-display] text-xl">{p.name}</h3>
          <p className="text-xs text-text/50">Practice (from the Tech Radar)</p>
        </div>
        <PillButton onClick={(toggle) => onSearch("status", p.status, toggle)} title={`Show every ${p.status} entry`}>
          <Badge tone={p.status === "active" ? "accent" : "warn"}>{p.status}</Badge>
        </PillButton>
      </div>

      <FlagsSection type="practice" entry={p} onChanged={onChanged} />

      <Markdown text={p.what} />

      <div>
        <h4 className={HEADING}>Same subject as a standard</h4>
        {choosing === null ? (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {p.same_as_standard ? (
              <span>The radar's view of {standardLink(p.same_as_standard)} — its ring shows on that standard.</span>
            ) : (
              <span className="text-text/60">Not the same as any standard.</span>
            )}
            <button
              onClick={() => setChoosing(p.same_as_standard ?? "")}
              className="cursor-pointer text-xs text-text/60 underline"
            >
              {p.same_as_standard ? "Change" : "Set"}
            </button>
            {p.same_as_standard && (
              <button
                onClick={() => saveSameAs(null)}
                disabled={busy}
                className="cursor-pointer text-xs text-text/60 underline"
              >
                Clear
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <Select value={choosing} onChange={setChoosing} placeholder="Choose a standard…" className="max-w-80">
              {standards
                .filter((s) => !s.radar || s.radar.practice_id === p.id)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </Select>
            <Button onClick={() => saveSameAs(choosing || null)} isDisabled={busy || !choosing}>
              Save
            </Button>
            <Button onClick={() => setChoosing(null)} isDisabled={busy}>
              Cancel
            </Button>
          </div>
        )}
      </div>

      <div>
        <h4 className={HEADING}>A recognised way to meet</h4>
        {p.related_standards.length === 0 ? (
          <p className="text-sm text-text/60">No standard confirmed yet.</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {p.related_standards.map((sid) => (
              <li key={sid}>{standardLink(sid)}</li>
            ))}
          </ul>
        )}
      </div>

      {p.drafted_standards.length > 0 && (
        <div>
          <h4 className={HEADING}>Drafted links — awaiting review</h4>
          <p className="mb-1.5 text-xs text-text/50">
            Proposed by the model. Until confirmed they only make this practice a “may help” suggestion.
          </p>
          <ul className="flex flex-col gap-1.5">
            {p.drafted_standards.map((d) => (
              <li key={d.id} className="flex items-start justify-between gap-3 rounded border border-line p-2 text-sm">
                <div>
                  <p>{standardLink(d.id)}</p>
                  <p className="text-text/60">{d.reason}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button onClick={() => change("confirm", d.id)} isDisabled={busy}>
                    Confirm
                  </Button>
                  <Button variant="danger" onClick={() => change("dismiss", d.id)} isDisabled={busy}>
                    Dismiss
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && <p className="text-sm text-warn">{error}</p>}

      <RadarSection tool={p} noun="practice" onSearch={onSearch} />
    </Card>
  );
}
